/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { SubtenantResponse, PatchSubtenant, UpdateSubtenantResponse, ApiError, UpdateSubtenantData } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type UpdateSubtenantMutation = PatchSubtenant & { id: string };

export type UpdateSubtenantMutationConfig = {
  onSuccess?: (data: SubtenantResponse, variables: UpdateSubtenantMutation) => void;
  onError?: (error: unknown, variables: UpdateSubtenantMutation) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: UpdateSubtenantMutation) => Promise<unknown> | void;
};

/**
 * Hook for updating subtenant by ID through the management plane API.
 *
 * @param config.onSuccess - Called after successful subtenant update
 * @param config.onError - Called if update fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for updating subtenant
 *
 * @example
 * ```tsx
 * const { mutate } = useUpdateSubtenantMutation({
 *   onSuccess: (data) => console.log('Subtenant updated:', data),
 *   onError: (error) => console.error('Subtenant update failed:', error)
 * });
 *
 * // Update Subtenant
 * mutate({
 *   subtenantId: '9fb763f7-33de-4969-a7a0-b808f4637318',
 *   requestBody: { name: 'Updated Subtenant Name' },
 * });
 * ```
 */

type UpdateSubtenantPayload = {
  id: UpdateSubtenantData['id'];
  requestBody: Omit<UpdateSubtenantData['requestBody'], 'badgeColor'>;
};

export function useUpdateSubtenantMutation({ onSuccess, onError, onSettled, onMutate }: UpdateSubtenantMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, isError, error } = useMutation<UpdateSubtenantResponse, ApiError, UpdateSubtenantPayload>({
    mutationFn: ({ id: subtenantId, requestBody }) => {
      return mgmtPlaneAPIClient.subtenants.updateSubtenant({
        tenantId: tenantId!,
        id: subtenantId,
        requestBody,
      });
    },
    onSuccess,
    onError,
    onSettled,
    onMutate,
  });

  return { mutate, isError, error };
}
